import {
	differenceInDays,
	differenceInHours,
	differenceInMilliseconds,
	differenceInMinutes,
	differenceInSeconds,
} from "date-fns";
import { DateTime, Duration } from "luxon";

export function dateToStringWithoutTime(dateOrString: Date | string): string {
	return dateYYYYMMDD(dateOrString);
}

export function dateWithoutTime(dateOrString: Date | string): Date {
	return new Date(dateYYYYMMDD(dateOrString));
}

export function dateYYYYMMDD(dateOrString: Date | string): string {
	const date = typeof dateOrString === "string" ? new Date(dateOrString) : dateOrString;
	return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
		.getDate()
		.toString()
		.padStart(2, "0")}`;
}

/** The server's reference timezone. Trading/valuation dates are anchored here. */
const SERVER_ZONE = "Europe/Rome";

/**
 * Converts a calendar date picked in the user's local timezone into the
 * equivalent calendar date in the server timezone (Europe/Rome).
 *
 * The picked date is anchored at the *current* local time-of-day before the
 * conversion, so an action performed early in the morning by a user ahead of
 * Rome (e.g. Australia) can normalize to the previous day in Rome — matching
 * what the user perceives as "today" vs. what the server records.
 *
 * @param localDate a `YYYY-MM-DD` string as selected in the user's calendar
 * @returns the corresponding `YYYY-MM-DD` string in Europe/Rome
 */
export function localDateToServerDate(localDate: string): string {
	const now = DateTime.now();
	return DateTime.fromISO(localDate)
		.set({ hour: now.hour, minute: now.minute, second: now.second, millisecond: now.millisecond })
		.setZone(SERVER_ZONE)
		.toISODate() as string;
}

/**
 * Yesterday in the server timezone (Europe/Rome), expressed in the user's local
 * calendar as a `YYYY-MM-DD` string.
 *
 * Use as the latest selectable date for "accept in the past": picking it maps,
 * via {@link localDateToServerDate}, exactly to the server's yesterday — the
 * last fully-closed day — regardless of the user's timezone.
 */
export function serverYesterdayAsLocalDate(): string {
	return DateTime.now().setZone(SERVER_ZONE).minus({ days: 1 }).toLocal().toISODate() as string;
}

/**
 * Today in the server timezone (Europe/Rome), expressed in the user's local
 * calendar as a `YYYY-MM-DD` string.
 *
 * Companion to {@link serverYesterdayAsLocalDate}: use it when a date must be
 * *strictly before* the server's current day (e.g. an as-of view of the past).
 */
export function serverTodayAsLocalDate(): string {
	return DateTime.now().setZone(SERVER_ZONE).toLocal().toISODate() as string;
}

/**
 * Converts a UTC date/timestamp into the equivalent calendar date in the user's
 * local timezone, as a `YYYY-MM-DD` string.
 *
 * The input is interpreted as UTC (a bare `YYYY-MM-DD` is treated as UTC
 * midnight), so users behind/ahead of UTC may see the previous/next day.
 */
export function utcDateToLocalDate(dateOrString: Date | string): string {
	const dt =
		typeof dateOrString === "string"
			? DateTime.fromISO(dateOrString, { zone: "utc" })
			: DateTime.fromJSDate(dateOrString, { zone: "utc" });
	return dt.toLocal().toISODate() as string;
}

export function dateYYYYMMDD_utc(dateOrString: Date | string): string {
	const date = typeof dateOrString === "string" ? new Date(dateOrString) : dateOrString;
	return `${date.getUTCFullYear()}-${(date.getUTCMonth() + 1).toString().padStart(2, "0")}-${date
		.getUTCDate()
		.toString()
		.padStart(2, "0")}`;
}

export function dateYYYY_W(dateOrString: Date | DateTime | string): string {
	const date =
		typeof dateOrString === "string"
			? DateTime.fromISO(dateOrString)
			: dateOrString instanceof Date
				? DateTime.fromJSDate(dateOrString)
				: dateOrString;
	return `${date.weekYear}-W${date.weekNumber.toString().padStart(2, "0")}`;
}

export function date_WeekYear(dateOrString: Date | DateTime | string): number {
	const date =
		typeof dateOrString === "string"
			? DateTime.fromISO(dateOrString)
			: dateOrString instanceof Date
				? DateTime.fromJSDate(dateOrString)
				: dateOrString;
	return date.weekYear;
}

export function date_W(dateOrString: Date | DateTime | string): string {
	const date =
		typeof dateOrString === "string"
			? DateTime.fromISO(dateOrString)
			: dateOrString instanceof Date
				? DateTime.fromJSDate(dateOrString)
				: dateOrString;
	return `W${date.weekNumber.toString().padStart(2, "0")}`;
}

export function dateYYYY_W_to_monday(yyyy_w: string): string {
	return dateYYYYMMDD(DateTime.fromISO(yyyy_w).toJSDate());
}

/**
 *
 * @param from e.g. 2020-W01
 * @param to e.g. 2021-W52
 * @returns an array containing all weeks in the range from (included) - to (excluded)
 */
export function weekRange(from: string, to: string): string[] {
	const output: string[] = []; // output will have 52 or 53 weeks depending on the year
	let cursor = DateTime.fromISO(from).plus(Duration.fromObject({ hours: 12 }));
	while (dateYYYY_W(cursor) !== to) {
		output.push(dateYYYY_W(cursor));
		cursor = cursor.plus(Duration.fromObject({ day: 7 }));
	}
	return output;
}

/**
 *
 * @param from date
 * @param to date
 * @returns the difference in number without consider time
 */
export function differenceInDaysISO(from?: string, to?: string): number {
	if (!from || !to) {
		return 0;
	}
	const duration = differenceInDays(
		new Date(new Date(from).toISOString().split("T")[0]),
		new Date(new Date(to).toISOString().split("T")[0]),
	);
	return duration;
}

export function differenceInHoursISO(from?: string, to?: string): number {
	if (!from || !to) {
		return 0;
	}
	const duration = differenceInHours(
		new Date(new Date(from).toISOString().split("T")[0]),
		new Date(new Date(to).toISOString().split("T")[0]),
	);
	return duration;
}

export function differenceInMinutesISO(from?: string, to?: string): number {
	if (!from || !to) {
		return 0;
	}
	const duration = differenceInMinutes(
		new Date(new Date(from).toISOString().split("T")[0]),
		new Date(new Date(to).toISOString().split("T")[0]),
	);
	return duration;
}

export function differenceInSecondsISO(from?: string, to?: string): number {
	if (!from || !to) {
		return 0;
	}
	const duration = differenceInSeconds(
		new Date(new Date(from).toISOString().split("T")[0]),
		new Date(new Date(to).toISOString().split("T")[0]),
	);
	return duration;
}

export function differenceInMillisecondsISO(from?: string, to?: string): number {
	if (!from || !to) {
		return 0;
	}
	const duration = differenceInMilliseconds(
		new Date(new Date(from).toISOString().split("T")[0]),
		new Date(new Date(to).toISOString().split("T")[0]),
	);
	return duration;
}

export function dateISOtoLocalTimezone(date: string): {
	localDate: DateTime<true> | DateTime<false>;
	localJsDate: Date;
} {
	const isoDate = DateTime.fromISO(date, { zone: "Europe/Rome", setZone: true });
	return { localDate: isoDate.toLocal(), localJsDate: isoDate.toLocal().toJSDate() };
}
